import RedundantLink from 'components/accesibility/redundant-link';
import styles from './publication-search-card.module.scss';
import { routeForDocument } from 'lib/routes';
import { Link } from 'src/navigation';
import MarkdownParser from 'components/rich-text/markdown-parser';
import Badge from 'components/badge';
import TrackedDownloadButton from 'components/controls/links/download-button/tracked-download-button';
import { type ElasticPublication, type ElasticJournal } from '@metmuseum/types';
import SanityImage from '@sanity-image';
import useAnimateIntoView from 'hooks/use-animate-into-view';
import classNames from 'node_modules/classnames';

const PublicationSearchCard = ({ publication }: Props) => {
	const {
		sanityId,
		title,
		description,
		authors,
		authorsDisplay,
		overrideAuthorsDisplayLogic,
		slug,
		datePublished,
		pdfUrl,
		coverImage,
		thumbnailUrl,
	} = publication;

	const {
		ref: animateRef,
		className: animateClassName,
		style: animateStyle,
	} = useAnimateIntoView();

	const displayAuthors = overrideAuthorsDisplayLogic
		? authorsDisplay
		: authors?.length > 10
			? 'Various authors'
			: authorsDisplay;
	const yearPublished =
		typeof datePublished === 'number'
			? new Date(datePublished).getUTCFullYear().toString()
			: false;

	return (
		<li
			ref={animateRef}
			style={animateStyle}
			className={classNames(styles.cardWrapper, animateClassName)}
		>
			<div className={styles.card}>
				<div className={styles.imageWrapper}>
					{/* TODO: should we the NextSanityImage optimization library for this? */}
					{coverImage ? (
						<SanityImage
							className={styles.image}
							image={coverImage}
							sizes="250px"
						/>
					) : (
						thumbnailUrl && (
							/* eslint-disable @next/next/no-img-element */
							<img className={styles.image} src={thumbnailUrl} alt="" />
							/* eslint-enable @next/next/no-img-element */
						)
					)}
					<RedundantLink
						href={routeForDocument({
							slug: { current: slug },
							_type: 'publication',
						})}
					/>
				</div>
				<div className={styles.body}>
					<div className={styles.mainText}>
						<div>
							<h4 className={styles.heading}>
								<Link
									href={routeForDocument({
										slug: { current: slug },
										_type: 'publication',
									})}
								>
									<MarkdownParser>{title}</MarkdownParser>
								</Link>
							</h4>
							{authorsDisplay && (
								<div className={styles.authors}>{displayAuthors}</div>
							)}
							{yearPublished && (
								<div className={styles.date}>{yearPublished}</div>
							)}
						</div>
						<div className={styles.description}>{description}</div>
					</div>
					{pdfUrl && (
						<div className={styles.badges}>
							<div className={styles.badgeWrapper}>
								<Badge text="Free to download" color="var(--color-grey-900)" />
							</div>
							<TrackedDownloadButton
								analytics={{
									contentType: 'MetPubs',
									contentBlock: 'Search List',
									contentName: title,
									contentId: sanityId,
								}}
								file={{ url: pdfUrl }}
								target="_blank"
								rel="noopener noreferrer"
							/>
						</div>
					)}
				</div>
			</div>
		</li>
	);
};

interface Props {
	publication: ElasticPublication | ElasticJournal;
}

export default PublicationSearchCard;
